import { useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {
  Dialog,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import ReceiverForm from "../ReceiverForm/ReceiverForm";

const noteFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
    "& fieldset": {
      borderColor: "#e5e7eb",
    },
    "&:hover fieldset": {
      borderColor: "#d1d5db",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(249, 115, 22, 0.12)",
      "& fieldset": {
        borderColor: "#f97316",
        borderWidth: "1px",
      },
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    color: "#6b7280",
  },
};

const MangoBookingModal = ({
  open,
  onClose,
  onContinue,
  loading = false,
  additionalInfo,
  updateFields,
  fetchedFormData,
  receiverFormProps,
}) => {
  const receiverFormRef = useRef(null);

  const handleContinue = async () => {
    const contactResult = receiverFormRef.current?.validateContact?.();
    if (contactResult && !contactResult.valid) {
      return;
    }

    const postCodeResult =
      await receiverFormRef.current?.validatePostCode?.();
    if (postCodeResult && !postCodeResult.valid) {
      return;
    }
    onContinue?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      componentsProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(17, 24, 39, 0.45)",
            backdropFilter: "blur(6px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          maxHeight: "90vh",
          boxShadow:
            "0 20px 40px -12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <div className="flex flex-col max-h-[90vh] bg-white">
        {/* Header — light, with accent strip */}
        <div className="relative shrink-0 border-b border-gray-100 bg-white px-5 py-4">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-orange-500 rounded-r-full" />
          <div className="flex items-center justify-between pl-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
                Mango Booking Details
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Step 1 of 2 · Delivery details
              </p>
            </div>
            <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              sx={{
                color: "#9ca3af",
                "&:hover": { bgcolor: "#f3f4f6", color: "#374151" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-gray-50/60 px-4 py-4 sm:px-5 sm:py-5">
          {/* Info tip */}
          <div className="mb-4 flex gap-3 rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3">
            <InfoOutlinedIcon
              sx={{ fontSize: 20, color: "#d97706", mt: "2px", flexShrink: 0 }}
            />
            <p className="text-sm text-amber-900/90 leading-relaxed">
              Sender, route, box, and quantity are already set from your
              quotation. Add receiver details and a note below.
            </p>
          </div>

          {/* Receiver */}
          <section className="mb-4 rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
              <PersonOutlineOutlinedIcon
                sx={{ fontSize: 18, color: "#f97316" }}
              />
              <h3 className="text-sm font-semibold text-gray-800">
                Receiver information
              </h3>
            </div>
            <ReceiverForm
              ref={receiverFormRef}
              embedded
              {...receiverFormProps}
              fetchedFormData={fetchedFormData}
              updateFields={updateFields}
            />
          </section>

          {/* Note */}
          <section className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <StickyNote2OutlinedIcon
                sx={{ fontSize: 18, color: "#f97316" }}
              />
              <h3 className="text-sm font-semibold text-gray-800">
                Parcel note <span className="text-red-500">*</span>
              </h3>
            </div>
            <TextField
              sx={noteFieldStyle}
              fullWidth
              multiline
              minRows={3}
              maxRows={5}
              required
              id="mango-parcel-note"
              label="Note"
              variant="outlined"
              value={additionalInfo}
              onChange={(e) =>
                updateFields({ additionalInfo: e.target.value })
              }
              placeholder="e.g. Handle with care, call before delivery..."
            />
          </section>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-4 sm:px-5">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto px-5 py-2.5 text-sm font-medium text-gray-600 rounded-lg
                hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleContinue}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5
                text-sm font-semibold text-white rounded-lg
                bg-gradient-to-r from-orange-500 to-amber-500
                shadow-md shadow-orange-500/20
                hover:shadow-lg hover:shadow-orange-500/30 hover:from-orange-600 hover:to-amber-600
                active:scale-[0.98] transition-all duration-200
                disabled:opacity-60 disabled:pointer-events-none"
            >
              {loading ? (
                <>
                  <CircularProgress size={18} sx={{ color: "white" }} />
                  Calculating...
                </>
              ) : (
                <>
                  Continue to create shipment
                  <ArrowForwardRoundedIcon
                    sx={{
                      fontSize: 18,
                      transition: "transform 0.2s",
                      ".group:hover &": { transform: "translateX(2px)" },
                    }}
                  />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default MangoBookingModal;
